"use client"

import { ReactNode } from 'react'
import { usePermissions } from '@/contexts/PermissionContext'
import { hasAnyPermission, hasPermission } from '@/lib/permissions'

interface PermissionGuardProps {
  permission: string | string[]
  children: ReactNode
  fallback?: ReactNode
}

/**
 * PermissionGuard component that conditionally renders children based on user permissions.
 * This component integrates with the RBAC system to check if the current user has the required permission.
 */
export default function PermissionGuard({
  permission,
  children,
  fallback = null
}: PermissionGuardProps) {
  const { permissions, loading } = usePermissions()

  if (loading) {
    return <>{fallback}</>
  }

  // If user is not authenticated, don't render anything
  if (!permissions) {
    return <>{fallback}</>
  }

  // If user has no permissions array, deny access (no hardcoded fallbacks)
  const effectivePermissions = permissions.effective_permissions || []
  if (effectivePermissions.length === 0) {
    return <>{fallback}</>
  }

  // Check if user has the required permission
  const hasAccess = Array.isArray(permission)
    ? hasAnyPermission({ permissions: effectivePermissions }, permission)
    : hasPermission({ permissions: effectivePermissions }, permission)

  // Render children if user has permission, otherwise render fallback
  return <>{hasAccess ? children : fallback}</>
}

